import { ErrorLike } from '@apollo/client';
import { QueryResult } from '@apollo/client/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  Attachment,
  FilterGroupingEnum,
  InterventionAction,
  InterventionActionStatusEnum,
  InterventionAssetPrefillDataQuery,
  InterventionAssetPrefillDataQueryVariables,
  InterventionCreateInputObject,
  InterventionSiteAreaPrefillDataQuery,
  InterventionSiteAreaPrefillDataQueryVariables,
  InterventionSitePrefillDataQuery,
  InterventionSitePrefillDataQueryVariables,
  InterventionStatusEnum,
  InterventionUpdateInputObject,
  useInterventionAssetPrefillDataQuery,
  useInterventionSiteAreaPrefillDataQuery,
  useInterventionSitePrefillDataQuery,
} from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import toast from '@lib/components/Toast/Toast';
import getAppUrlWithDomain from '@lib/utils/getAppUrlWithDomain';
import useInterventionQueryHook, {
  UseAssetInterventionQueryHookResult,
} from 'utils/fetch/hooks/useInterventionQueryHook';
import { APP_URLS } from 'constants/urls';
import useTaskQueryHook, {
  UseTaskQueryHookResult,
} from 'utils/fetch/hooks/useTaskQueryHook';
import FieldNames from '@lib/enums/fieldNames/interventionFieldNames';
import useUsersQueryHook, {
  UseUsersQueryHookResult,
} from 'utils/fetch/hooks/useUsersQueryHook';
import useSearchParam from '@lib/hooks/useSearchParam';
import { GetUrlParams, PATH_ID } from '@lib/enums/urls';
import useInterventionActionsQueryHook, {
  UseAssetInterventionsQueryHookResult,
} from 'utils/fetch/hooks/useInterventionActionsQueryHook';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import useFilterParams from '@lib/hooks/useFilterParams';
import getInterventionPreparedCreateDataFromValues from '@lib/utils/intervention/getInterventionPreparedCreateDataFromValues';
import { UseModalManagementResult } from './useModalManagement';
import useMutations from './useMutations';
import getInterventionPreparedUpdateDataFromValues from './getInterventionPreparedUpdateDataFromValues';
import { InterventionViewMode } from '../enums';

const assigneeRedirectUrl = getAppUrlWithDomain(
  APP_URLS.app.interventions.view.getDynamicPath({}),
).replace(PATH_ID, ':interventionId');

type Args = {
  useModalManagementResult: UseModalManagementResult;
  viewMode: InterventionViewMode;
  // Selects `intervention.task` — only for users with TASKS_MANAGEMENT read.
  withTask?: boolean;
};

export type UseInterventionActionsResult = {
  id: string;
  interventionActionsQueryHookResult: UseAssetInterventionsQueryHookResult;
  interventionAssetPrefillDataQueryHookResult: QueryResult<
    InterventionAssetPrefillDataQuery,
    InterventionAssetPrefillDataQueryVariables
  >;
  interventionQueryHookResult: UseAssetInterventionQueryHookResult;
  interventionSiteAreaPrefillDataQueryHookResult: QueryResult<
    InterventionSiteAreaPrefillDataQuery,
    InterventionSiteAreaPrefillDataQueryVariables
  >;
  interventionSitePrefillDataQueryHookResult: QueryResult<
    InterventionSitePrefillDataQuery,
    InterventionSitePrefillDataQueryVariables
  >;
  loading: boolean;
  mutationError: ErrorLike | undefined;
  onActionStatusChangeHandler: (
    id: string,
    newStatus: InterventionActionStatusEnum,
  ) => void;
  onAttachFiles: (files: Attachment[]) => void;
  interventionActionCloseLoading: boolean;
  interventionActionDestroyLoading: boolean;
  interventionCloseLoading: boolean;
  interventionDestroyLoading: boolean;
  onConfirmedInterventionActionClose: () => void;
  onConfirmedInterventionActionDestroy: () => void;
  onConfirmedInterventionClose: () => void;
  onConfirmedInterventionDestroy: () => void;
  onStatusChangeHandler: (
    id: string,
    newStatus: InterventionStatusEnum,
  ) => void;
  onSubmit: (values: Values) => void;
  onViewChange: (newViewMode: string) => void;
  onViewTask: (taskId: string) => void;
  openCreateActionPage: () => void;
  openEditActionPage: (interventionAction: InterventionAction) => void;
  taskQueryHookResult: UseTaskQueryHookResult;
  usersQueryHookResult: UseUsersQueryHookResult;
};

export default function useInterventionActions({
  viewMode,
  useModalManagementResult,
  withTask = false,
}: Args): UseInterventionActionsResult {
  const navigate = useNavigate();
  const [searchQuery] = useSearchParam();
  const [filterParams] = useFilterParams();
  const [paginationParams] = usePaginationParams();
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const useMutationsResult = useMutations();

  const {
    [GetUrlParams.SiteId]: siteIdUrlParam,
    [GetUrlParams.SiteAreaId]: siteAreaIdUrlParam,
    [GetUrlParams.AssetId]: assetIdUrlParam,
  } = filterParams;

  // Intervention
  const interventionQueryHookResult = useInterventionQueryHook({
    id,
    skip: viewMode === InterventionViewMode.Add,
    withTask,
    fetchPolicy: 'cache-and-network',
  });

  const interventionActionsQueryHookResult = useInterventionActionsQueryHook({
    paginationParams,
    orderingParams: paginationParams,
    searchQuery,
    skip: viewMode !== InterventionViewMode.ViewActions,
    interventionId: id,
  });

  const interventionSitePrefillDataQueryHookResult =
    useInterventionSitePrefillDataQuery({
      skip: !siteIdUrlParam,
      variables: {
        siteFindBy: {
          id: siteIdUrlParam,
        },
      },
    });
  const interventionSiteAreaPrefillDataQueryHookResult =
    useInterventionSiteAreaPrefillDataQuery({
      skip: !siteIdUrlParam || !siteAreaIdUrlParam,
      variables: {
        siteFindBy: {
          id: siteIdUrlParam,
        },
        siteAreaFindBy: {
          id: siteAreaIdUrlParam,
        },
      },
    });
  const interventionAssetPrefillDataQueryHookResult =
    useInterventionAssetPrefillDataQuery({
      skip: !assetIdUrlParam,
      variables: {
        assetFindBy: {
          id: assetIdUrlParam,
        },
      },
    });

  // Task
  const interventionTaskId =
    _get(interventionQueryHookResult, 'data.task.id') || '';
  const taskQueryHookResult = useTaskQueryHook({
    id: interventionTaskId,
    skip: viewMode !== InterventionViewMode.View,
  });

  // Users (intervention assignees) for view
  const usersQueryHookResult = useUsersQueryHook({
    paginationParams: {
      [GetUrlParams.Page]: '1',
      [GetUrlParams.PerPage]: '4',
    },
    skip: !(
      viewMode === InterventionViewMode.View ||
      viewMode === InterventionViewMode.ViewActions ||
      viewMode === InterventionViewMode.ViewAction
    ),
    skipGetAllIds: viewMode !== InterventionViewMode.Edit,
    additionalFilters: [
      {
        interventionAssigneesInterventionId: {
          grouping: FilterGroupingEnum.Or,
          predicate: { eq: id },
        },
      },
    ],
  });
  const { allIds: assigneeIds } = usersQueryHookResult;

  const onInterventionCreate = (values: InterventionCreateInputObject) => {
    useMutationsResult.interventionCreateMutation({
      variables: {
        attributes: values,
        assigneeRedirectUrl,
      },
      onCompleted: (res) => {
        const newId = res?.data?.id;
        toast({ content: t('create-intervention-success') });
        if (newId) {
          navigate(
            APP_URLS.app.interventions.view.getDynamicPath({
              pathParts: { id: newId },
            }),
          );
        }
      },
    });
  };

  const onInterventionUpdate = (values: InterventionUpdateInputObject) => {
    useMutationsResult.interventionUpdateMutation({
      variables: {
        id,
        attributes: values,
        assigneeRedirectUrl,
      },
      onCompleted: () => {
        toast({ content: t('update-intervention-success') });
        navigate(
          APP_URLS.app.interventions.view.getDynamicPath({
            pathParts: { id },
          }),
        );
      },
    });
  };

  const onSubmit = (values: Values) => {
    if (viewMode === InterventionViewMode.Add) {
      onInterventionCreate(getInterventionPreparedCreateDataFromValues(values));
    } else if (viewMode === InterventionViewMode.Edit) {
      onInterventionUpdate(
        getInterventionPreparedUpdateDataFromValues(values, {
          ...interventionQueryHookResult.data,
          [FieldNames.Assignee]: assigneeIds.map(({ id: assigneeId }) => ({
            value: assigneeId,
          })),
        } as Values),
      );
    }
  };

  const onConfirmedInterventionDestroy = () => {
    useMutationsResult.interventionDestroyMutation({
      variables: { id },
      onCompleted: () => {
        useModalManagementResult.closeAllModals();
        toast({ content: t('destroy-intervention-success') });
        navigate(APP_URLS.app.interventions.index.getPathWithQuery());
      },
    });
  };

  const onConfirmedInterventionClose = () => {
    useMutationsResult.interventionBulkCloseMutation({
      variables: { ids: [id] },
      onCompleted: () => {
        useModalManagementResult.closeAllModals();
        toast({ content: t('close-intervention-success') });
        interventionQueryHookResult.refetch();
      },
    });
  };

  const onConfirmedInterventionActionClose = () => {
    const interventionActionId =
      useModalManagementResult.isInterventionActionCloseModalOpened?.id;
    if (interventionActionId) {
      useMutationsResult.interventionActionUpdateMutation({
        variables: {
          id: interventionActionId,
          interventionId: id,
          attributes: { status: InterventionActionStatusEnum.Closed },
          assigneeRedirectUrl,
          mentionRedirectUrl: getAppUrlWithDomain(
            APP_URLS.app.interventions.view.getDynamicPath({
              pathParts: { id: interventionActionId },
            }),
          ),
        },
        onCompleted: () => {
          useModalManagementResult.closeAllModals();
          toast({ content: t('close-intervention-success') });
          interventionQueryHookResult.refetch();
        },
      });
    }
  };

  const onConfirmedInterventionActionDestroy = () => {
    const actionId =
      useModalManagementResult.isInterventionActionDestroyModalOpened?.id;
    if (actionId) {
      useMutationsResult.interventionActionDestroyMutation({
        variables: { id: actionId, interventionId: id },
        onCompleted: () => {
          useModalManagementResult.closeAllModals();
          toast({ content: t('destroy-intervention-action-success') });
          if (viewMode === InterventionViewMode.ViewActions) {
            interventionActionsQueryHookResult.refetch();
            // `interventionActionDestroy` resolves to a scalar, so nothing in
            // the response lowers the `actionsCounter` behind the Actions tab
            // — refetch the intervention query that feeds it. Not needed on
            // the ViewAction branch below, which navigates back and remounts.
            interventionQueryHookResult.refetch();
          } else if (viewMode === InterventionViewMode.ViewAction) {
            navigate(
              APP_URLS.app.interventions.view.getDynamicPath({
                pathParts: { id },
              }),
            );
          }
        },
      });
    }
  };

  const onViewChange = (newViewMode: string) => {
    if (newViewMode === InterventionViewMode.View) {
      navigate(
        APP_URLS.app.interventions.view.getDynamicPath({
          pathParts: { id },
        }),
      );
    } else if (newViewMode === InterventionViewMode.ViewActions) {
      navigate(
        APP_URLS.app.interventions.viewActions.getDynamicPath({
          pathParts: { id },
        }),
      );
    } else if (newViewMode === InterventionViewMode.Edit) {
      navigate(
        APP_URLS.app.interventions.edit.getDynamicPath({
          pathParts: { id },
        }),
      );
    }
  };

  const onViewTask = (taskId: string) => {
    navigate(
      APP_URLS.app.tasks.view.getDynamicPath({
        pathParts: {
          id: taskId,
        },
      }),
    );
  };

  const onStatusChangeHandler = (
    interventionId: string,
    newStatus: InterventionStatusEnum,
  ) => {
    if (id === interventionId && newStatus) {
      if (newStatus === InterventionStatusEnum.Closed) {
        useModalManagementResult.openInterventionCloseModal();
      } else {
        onInterventionUpdate({ status: newStatus });
      }
    }
  };

  const onActionStatusChangeHandler = (
    interventionActionId: string,
    newStatus: InterventionActionStatusEnum,
  ) => {
    if (interventionActionId && newStatus) {
      if (newStatus === InterventionActionStatusEnum.Closed) {
        useModalManagementResult.openInterventionActionCloseModal({
          id: interventionActionId,
        } as InterventionAction);
      } else {
        useMutationsResult.interventionActionUpdateMutation({
          variables: {
            id: interventionActionId,
            interventionId: id,
            attributes: { status: newStatus },
            assigneeRedirectUrl,
            mentionRedirectUrl: getAppUrlWithDomain(
              APP_URLS.app.interventions.view.getDynamicPath({
                pathParts: { id: interventionActionId },
              }),
            ),
          },
          onCompleted: () => {
            toast({ content: t('update-intervention-action-success') });
          },
        });
      }
    }
  };

  const onAttachFiles = (files: Attachment[]) => {
    if (Array.isArray(files) && files.length) {
      onInterventionUpdate(
        getInterventionPreparedUpdateDataFromValues(
          { [FieldNames.AttachmentsAttached]: files },
          interventionQueryHookResult.data as Values,
        ),
      );
    }
  };

  const openCreateActionPage = () => {
    navigate(
      APP_URLS.app.interventions.actionAdd.getDynamicPath({
        pathParts: { id },
      }),
    );
  };

  const openEditActionPage = (interventionAction: InterventionAction) => {
    navigate(
      APP_URLS.app.interventions.actionEdit.getDynamicPath({
        pathParts: { id, actionId: interventionAction.id },
      }),
    );
  };

  const loading =
    interventionQueryHookResult.loading ||
    useMutationsResult.interventionCreateMutationResult.loading ||
    useMutationsResult.interventionUpdateMutationResult.loading;

  const mutationError =
    useMutationsResult.interventionCreateMutationResult.error ||
    useMutationsResult.interventionUpdateMutationResult.error;

  return {
    id,
    interventionActionCloseLoading:
      useMutationsResult.interventionActionUpdateMutationResult.loading,
    interventionActionDestroyLoading:
      useMutationsResult.interventionActionDestroyMutationResult.loading,
    interventionCloseLoading:
      useMutationsResult.interventionBulkCloseMutationResult.loading,
    interventionDestroyLoading:
      useMutationsResult.interventionDestroyMutationResult.loading,
    interventionActionsQueryHookResult,
    interventionAssetPrefillDataQueryHookResult,
    interventionQueryHookResult,
    interventionSiteAreaPrefillDataQueryHookResult,
    interventionSitePrefillDataQueryHookResult,
    loading,
    mutationError,
    onActionStatusChangeHandler,
    onAttachFiles,
    onConfirmedInterventionActionClose,
    onConfirmedInterventionActionDestroy,
    onConfirmedInterventionClose,
    onConfirmedInterventionDestroy,
    onStatusChangeHandler,
    onSubmit,
    onViewChange,
    onViewTask,
    openCreateActionPage,
    openEditActionPage,
    taskQueryHookResult,
    usersQueryHookResult,
  };
}
